import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ContactSendingFormNode} from "../contact-sending/contactSending";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from "@angular/forms";
import {EditPermissions, NodeBuilder} from "../../../../../../../shared/models";

import { ContactTypes, ContactDropdownButtonComponent } from "../../../../../template/contact-dropdown-button/contact-dropdown-button.component";
import cloneDeep from 'lodash/cloneDeep';
import { DeleteContact, ContactsTemplateComponent } from "../../../../../template/contacts-template/contacts-template.component";
import { ClickatellExpansionPanelComponent } from '../../../../../../../shared/components/clickatell-expansion-panel/clickatell-expansion-panel.component';
import { MatExpansionPanelTitle } from '@angular/material/expansion';
import { NgTemplateOutlet, NgStyle, KeyValuePipe } from '@angular/common';
import { ActiveFormDirective } from '../../../../../../../shared/directives/active-form/active-form.directive';
import { CMSComponent } from '../../../../../../../shared/components/cms/cms.component';
import { NextSelectorComponent } from '../../../../json-to-from/next-selector/next-selector.component';

const NODE_TYPE = 'contactSending'

@Component({
    selector: 'app-contact-sending',
    templateUrl: './contact-sending.component.html',
    styleUrls: ['./contact-sending.component.scss'],
    imports: [ClickatellExpansionPanelComponent, MatExpansionPanelTitle, ReactiveFormsModule, NgTemplateOutlet, ActiveFormDirective, ContactDropdownButtonComponent, ContactsTemplateComponent, NgStyle, CMSComponent, NextSelectorComponent, KeyValuePipe]
})
export class ContactSendingComponent extends NodeBuilder implements OnInit {
  @ViewChild('countryFilter') countryFilter: ElementRef;

  constructor(
      private fb: UntypedFormBuilder,
  ) {
    super(NODE_TYPE)
  }

  sendingParams: UntypedFormGroup;
  contactInfo: UntypedFormGroup;
  contactInfoRawData = {}
  canEdit: EditPermissions;
  stepUniqueID: string = '';
  addNewFieldSet = 0;

  ngOnInit(): void {
    this.contactInfo = this.fb.group({
      addresses: new UntypedFormArray([]),
      emails: new UntypedFormArray([]),
      org: new UntypedFormArray([]),
      phones: new UntypedFormArray([]),
      urls: new UntypedFormArray([]),
      birthdays: new UntypedFormArray([])
    });
    this.contactInfoRawData = this.contactInfo.getRawValue()
  }

  loadContactInfoContent(formData) {
    const contact = formData.menuParams.contact
    if(contact.addresses.length > 0) {
      for (let address of contact.addresses) {
        this.addContact(ContactTypes.ADDRESS, address);
      }
    }
    if(contact.emails.length > 0) {
      for (let email of contact.emails) {
        this.addContact(ContactTypes.EMAIL, email);
      }
    }
    if(contact.urls.length > 0) {
      for (let url of contact.urls) {
          this.addContact(ContactTypes.URL, url);
      }
    }
    if(contact.birthdays.length > 0) {
      for (let birthday of contact.birthdays) {
          this.addContact(ContactTypes.BIRTHDAY, birthday);
      }
    }
    if(contact.org.length > 0) {
      for (let org of contact.org) {
          this.addContact(ContactTypes.ORGANISATION, org);
      }
    }
    if(contact.phones.length > 0) {
      for (let phone of contact.phones) {
          this.addContact(ContactTypes.PHONE, phone);
      }
    }
  }

  set buildForm(stepData: any) {
    this.stepUniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';
    const formData = ContactSendingFormNode.toForm(stepData);
    if (!stepData) {
      return;
    }
    this.buildMenuParamsForm(formData);
  }

  checkIfAddNewFieldIsTouchedOrDirty() {
    return !(this.sendingParams.get('addNewField').touched || this.sendingParams.get('addNewField').dirty);
  }

  get getStepData(): {} {
    const menuParams = this.sendingParams.getRawValue();
    menuParams.addresses = this.contactInfo.controls["addresses"].value;
    menuParams.urls = this.contactInfo.controls["urls"].value;
    menuParams.emails = this.contactInfo.controls["emails"].value;
    menuParams.org = this.contactInfo.controls["org"].value;
    menuParams.phones = this.contactInfo.controls["phones"].value;
    menuParams.birthdays = this.contactInfo.controls["birthdays"].value;
    const contactSendingFormNode = new ContactSendingFormNode(this.nodeType);
    contactSendingFormNode.addMenuParams(menuParams);
    const nodeData = contactSendingFormNode.nodeData;
    nodeData['next'] = menuParams.next;
    if(this.stepUniqueID !== '') {
      nodeData['metadata'].stepUniqueID = this.stepUniqueID;
    }
    return nodeData;
  }

  isEmptyFormArray(): boolean {
    const formArrays = [
      this.contactInfo.get('addresses') as UntypedFormArray,
      this.contactInfo.get('emails') as UntypedFormArray,
      this.contactInfo.get('org') as UntypedFormArray,
      this.contactInfo.get('phones') as UntypedFormArray,
      this.contactInfo.get('urls') as UntypedFormArray,
      this.contactInfo.get('birthdays') as UntypedFormArray,
    ];
    return formArrays.every((formArray) => formArray.length === 0);
  }

  get isNodeValid(): boolean {
    this.sendingParams.markAllAsTouched();
    this.contactInfo.markAllAsTouched();
    if (!this.isEmptyFormArray()) {
      return (this.sendingParams.valid && this.contactInfo.valid);
    }
  }

  buildMenuParamsForm(formData) {
    this.sendingParams = ContactSendingFormNode.getMenuTemplate(this.canEdit);
    this.loadContactInfoContent(formData);
    this.contactInfoRawData = this.contactInfo.getRawValue()
    this.sendingParams.patchValue(formData.menuParams.contact.name);
    this.sendingParams.controls['next'].patchValue(formData.menuParams.next);
    this.sendingParams.controls['addNewField'].patchValue(formData.menuParams.addNewField);
  }

  deleteTemplate(_: DeleteContact) {
    this.addNewFieldSet--;
    this.contactInfoRawData = cloneDeep(this.contactInfo.getRawValue());
  }

  addContact(contact: ContactTypes, value: any = undefined) {
    let newContact = undefined;

    switch (contact) {
      case ContactTypes.ADDRESS:
        newContact = ContactSendingFormNode.getAddressTemplate(this.canEdit);
        break
      case ContactTypes.BIRTHDAY:
        newContact = ContactSendingFormNode.getBirthdayTemplate(this.canEdit);
        break
      case ContactTypes.EMAIL:
        newContact = ContactSendingFormNode.getEmailTemplate(this.canEdit);
        break
      case ContactTypes.ORGANISATION:
        newContact = ContactSendingFormNode.getOrganisationTemplate(this.canEdit);
        break
      case ContactTypes.PHONE:
        newContact = ContactSendingFormNode.getPhoneTemplate(this.canEdit);
        break
      case ContactTypes.URL:
        newContact = ContactSendingFormNode.getUrlTemplate(this.canEdit);
        break
    }

    if (newContact !== undefined) {
      const contactList = this.contactInfo.controls[contact] as UntypedFormArray;

      if (value !== undefined) {
        newContact.patchValue(value);
      }

      contactList.push(newContact);
      this.contactInfoRawData = cloneDeep(this.contactInfo.getRawValue());
      this.addNewFieldSet++;
    }
  }

}
